import type { Cansize, ProductBasePaint } from 'js/api/Cache';
import type { CacheBasepaint } from 'js/redux/types/Cache';
import type { OrderCan } from 'js/redux/types/Order';
import _ from 'lodash';
import {
  EXTENDER_DEFAULT_MIN_PERCENT,
  FIXED_ABASEAMOUNT,
  ZERO_VOLUME_THRESHOLD_ML,
} from '../Constants';
import { get_cnt_for_product } from './Product';
import type { RootState } from 'js/redux/reducers';

type CntAmount = { volume: number; specificgravity: number };
type CntWithId = CntAmount & { cntid: number };
type CntWithIdOrCode = CntAmount & { cntid?: number | null; cntcode?: string };

/*
 * Functions for manipulation of formulas
 */

export function formula_volume_and_mass<T extends CntAmount>(
  cntinformula: ReadonlyArray<T>,
  scaling_factor = 1
): { volume: number; mass: number } {
  return cntinformula.reduce(
    (totals, cnt) => ({
      volume: totals.volume + cnt.volume * scaling_factor,
      mass: totals.mass + cnt.volume * scaling_factor * cnt.specificgravity,
    }),
    { volume: 0, mass: 0 }
  );
}

export function basevolume_of_can(
  base: CacheBasepaint,
  can: OrderCan,
  cansize?: Cansize | null
): number {
  const { nominalamount, gravimetric } = cansize || can;
  const baseamount = nominalamount * (can.fill || base.nominalfill || 1);
  if (gravimetric) return baseamount / base.specificgravity;
  else return baseamount;
}

export function can_scaling_factor(
  base: CacheBasepaint,
  can: OrderCan
): number {
  const basevolume = can.basevolume || basevolume_of_can(base, can);
  return (basevolume / FIXED_ABASEAMOUNT) * (base.coefficient || 1);
}

export function scale_to_can<T extends CntAmount>(
  cntinformula: Array<T>,
  base: CacheBasepaint,
  can: OrderCan
): Array<T> {
  let scaling = can_scaling_factor(base, can);
  if (base.isblendedbase && base.gravimetricfill) {
    // Special case for gravimetric blended base
    const total = _.sum(cntinformula.map((x) => x.volume * x.specificgravity));
    const baseamount = can.nominalamount * (can.fill || base.nominalfill || 1);
    scaling = total > ZERO_VOLUME_THRESHOLD_ML ? baseamount / total : 1;
  }
  return cntinformula.map((x) => ({ ...x, volume: x.volume * scaling }));
}

export function normalize_volumes<T extends CntAmount>(
  cnts: Array<T>,
  base: CacheBasepaint,
  can: OrderCan,
  freeDispense = false
): Array<T> | null {
  if (!freeDispense) {
    const scaling = can_scaling_factor(base, can);
    if (scaling) return cnts.map((x) => ({ ...x, volume: x.volume / scaling }));
    return null;
  } else return cnts.map((x) => ({ ...x, volume: x.volume }));
}

export function normalize_blended<T extends { volume: number }>(
  cntinformula: Array<T>
): Array<T> {
  // Normalize blended cntinformula to base amount
  const total = _.sum(cntinformula.map((x) => x.volume));
  if (total < ZERO_VOLUME_THRESHOLD_ML) {
    // fail silently
    return cntinformula;
  }
  const scaling = FIXED_ABASEAMOUNT / total;
  return cntinformula.map((x) => ({ ...x, volume: x.volume * scaling }));
}

export function update_extender_volume_from_redux<T extends CntWithId>(
  cnts: Array<T>,
  root: RootState
): Array<T> {
  const { order, cache } = root;
  const {
    base,
    can,
    product,
    item: { additionOnly },
  } = order;
  if (additionOnly || product?.extenderid == null) return cnts;
  return update_extender_volume(
    cnts,
    get_cnt_for_product(product.extenderid, cache.cntmap, product),
    base,
    can?.basevolume
  );
}
export function update_extender_volume<T extends CntWithId>(
  cnts: Array<T>,
  extender: { cntid: number; specificgravity: number } | null,
  base: ProductBasePaint | null,
  basevolume: number | null | undefined
): Array<T> {
  if (!extender || !base || !basevolume) return cnts;

  const without_extender = remove_extender(cnts, extender);

  let ext_vol;
  const maxfill = base.maxfill || 0;
  if (maxfill <= 0) {
    return without_extender;
  }
  if (base.gravimetricfill) {
    const ext_mass =
      maxfill * basevolume * base.specificgravity -
      _.sum(without_extender.map((x) => x.volume * x.specificgravity));
    ext_vol = ext_mass / extender.specificgravity;
  } else {
    ext_vol =
      maxfill * basevolume - _.sum(without_extender.map((x) => x.volume));
  }

  // apply min/max rule
  const rule = base.basecntrule.find((x) => x.cntid === extender.cntid);
  if (rule?.maxamount != null) {
    ext_vol = Math.min(ext_vol, (basevolume * rule.maxamount) / 100);
  }
  const minamount =
    rule?.minamount == null ? EXTENDER_DEFAULT_MIN_PERCENT : rule.minamount;
  if (ext_vol < (basevolume * minamount) / 100) {
    ext_vol = 0;
  }

  const result = without_extender;
  if (ext_vol > 0) {
    result.push({ ...extender, volume: ext_vol } as T);
  }
  return result;
}

export function remove_extender<T extends CntWithId>(
  cnts: Array<T>,
  extender?: { cntid: number }
): Array<T> {
  return extender ? cnts.filter((x) => x.cntid !== extender.cntid) : cnts;
}

/**
 * Combine edited and original formulas and calculate addition.
 */
export function merge_original_volumes<T extends CntWithIdOrCode>(
  cnts: Array<T>,
  initial_cnts: Array<T>,
  original_cnts: Array<T>,
  addition_scaling_factor = 1
): Array<
  T & { initialvolume: number; originalvolume: number; additionvolume: number }
> {
  const originals = _.fromPairs(
    original_cnts.map((cnt) => [surrogate_id(cnt), cnt])
  );
  const initials = _.fromPairs(
    initial_cnts.map((cnt) => [surrogate_id(cnt), cnt])
  );

  const r = [];
  for (const c of cnts) {
    const id = surrogate_id(c);
    const original = originals[id];
    if (original) {
      delete originals[id];
    }
    const originalvolume = original ? original.volume : 0;
    const initialvolume = initials[id]?.volume || 0;

    const additionvolume = c.volume * addition_scaling_factor - originalvolume;
    r.push({ ...c, initialvolume, originalvolume, additionvolume });
  }
  // If some of original_cnts have been removed from cnts they remain now in originals
  for (const original of Object.values(originals)) {
    if (original.volume)
      r.push({
        ...original,
        volume: 0,
        initialvolume: initials[surrogate_id(original)]?.volume || 0,
        originalvolume: original.volume,
        additionvolume: -original.volume,
      });
  }
  return r;
}

function surrogate_id(cnt: CntWithIdOrCode): string {
  return cnt.cntid == null ? `code=${cnt.cntcode}` : `id=${cnt.cntid}`;
}

export function base_addition_scaling_factor<T extends CntWithId>(
  cnts: Array<T>,
  original_cnts: Array<T>
): number {
  const originalVolumes = _.fromPairs(
    original_cnts.filter((x) => x.cntid != null).map((x) => [x.cntid, x.volume])
  );
  const factors = cnts.map((x) => {
    const original = originalVolumes[x.cntid] || 0;
    delete originalVolumes[x.cntid];
    return original / x.volume;
  });
  if (_.isEmpty(originalVolumes)) {
    return Math.max(1, ...factors);
  }
  // new formula is missing colorants:
  return Infinity;
}
